import React from "react";
import { clsx } from "clsx";

export type BadgeTone = "neutral" | "primary" | "gold" | "success" | "warning" | "danger" | "info";

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
    tone?: BadgeTone;
    size?: "sm" | "md";
    dot?: boolean;
}

const toneStyles: Record<BadgeTone, string> = {
    neutral: "bg-neutral-dark/8 text-neutral-dark dark:bg-white/10 dark:text-white",
    primary: "bg-primary/10 text-primary",
    gold: "bg-gold/15 text-gold-dark dark:text-gold-light",
    success: "bg-success/10 text-success-dark dark:text-success-light",
    warning: "bg-warning/15 text-warning-dark",
    danger: "bg-danger/10 text-danger-dark dark:text-danger-light",
    info: "bg-secondary/15 text-secondary-dark dark:text-secondary-light",
};

export const Badge: React.FC<BadgeProps> = ({
    tone = "neutral",
    size = "sm",
    dot = false,
    className,
    children,
    ...props
}) => (
    <span
        className={clsx(
            "inline-flex items-center gap-1.5 rounded-full font-medium",
            size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm",
            toneStyles[tone],
            className,
        )}
        {...props}
    >
        {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden /> : null}
        {children}
    </span>
);

export type StatusTone = BadgeTone;

export interface StatusBadgeProps extends Omit<BadgeProps, "tone"> {
    status: string;
    map?: Record<string, BadgeTone>;
}

const defaultStatusMap: Record<string, BadgeTone> = {
    pending: "warning",
    open: "info",
    active: "success",
    succeeded: "success",
    paid: "success",
    failed: "danger",
    cancelled: "neutral",
    closed: "neutral",
    synced: "success",
    offline: "warning",
    draft: "neutral",
    verified: "gold",
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, map = defaultStatusMap, ...props }) => {
    const tone = map[status.toLowerCase()] ?? "neutral";
    return (
        <Badge tone={tone} dot {...props}>
            {status}
        </Badge>
    );
};
